import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CardStatus, UserCard } from "../../../services/cards/mocks";

/**
 * Возвращает актуальный дизайн-шаблон карты для текущей темы.
 *   - если админ задал `imageUrl` (light) — рендерим его в light-теме;
 *   - если задан `imageUrlDark` — рендерим в dark-теме;
 *   - если задан только один из вариантов — используем его в обеих темах
 *     (fallback наверх).
 *   - если ничего не задано — возвращаем `null`, вызывающий код падёт на
 *     `card.gradient`.
 *
 * Экспортируется, чтобы использовать в мини-превью офферов
 * (`CardOffersSection.CardPreview`) без дублирования логики.
 */
export function pickCardTemplate(
  card: Pick<UserCard, "imageUrl" | "imageUrlDark">,
  mode: "light" | "dark",
): string | null {
  if (mode === "dark") return card.imageUrlDark || card.imageUrl || null;
  return card.imageUrl || card.imageUrlDark || null;
}

const STATUS_COLOR: Record<CardStatus, string> = {
  active: "#00CA48",
  processing: "#EAC200",
  frozen: "#8AB4FF",
};

function BrandLogo({ brand }: { brand: UserCard["brand"] }) {
  if (brand === "visa") {
    return (
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 800,
          fontStyle: "italic",
          letterSpacing: "1px",
          color: "#fff",
        }}
      >
        VISA
      </Typography>
    );
  }
  if (brand === "mastercard") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
        <Box
          sx={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            bgcolor: "#EB001B",
          }}
        />
        <Box
          sx={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            bgcolor: "#F79E1B",
            marginLeft: "-8px",
            mixBlendMode: "multiply",
          }}
        />
      </Box>
    );
  }
  return (
    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>
      UnionPay
    </Typography>
  );
}

interface CardVisualProps {
  card: UserCard;
  size?: "small" | "large";
  /** Внешнее переопределение статуса (например, для оптимистического UI при freeze/unfreeze) */
  statusOverride?: CardStatus;
}

export function CardVisual({
  card,
  size = "small",
  statusOverride,
}: CardVisualProps) {
  const { t } = useTranslation("cards");
  const theme = useTheme();
  const mode = theme.palette.mode === "dark" ? "dark" : "light";
  const template = pickCardTemplate(card, mode);
  const hasTemplate = Boolean(template);
  const effectiveStatus = statusOverride ?? card.status;
  const statusText: Record<CardStatus, string> = {
    active: t("visual.active"),
    processing: t("visual.processing"),
    frozen: t("visual.frozen"),
  };
  const isProcessing = effectiveStatus === "processing";
  const isFrozen = effectiveStatus === "frozen";
  const isLarge = size === "large";

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "20px",
        padding: isLarge ? "24px" : "20px",
        // Форм-фактор шаблонов, которые заливаются в админке — 500×295 px
        // (≈ 1.695:1). Раньше карта имела фиксированный `minHeight`, из-за
        // чего широкие шаблоны обрезались по краям. `aspectRatio` даёт
        // высоту, точно совпадающую с картинкой на любой ширине.
        aspectRatio: "500 / 295",
        color: "#fff",
        // Картинка-шаблон рисуется как background-image с cover-fit; если
        // шаблона нет — падаем на исторический gradient. Никакого подложного
        // цвета: если картинка не покрывает всю область, увидим сам layout
        // (например, тёмный фон приложения).
        background: hasTemplate ? "transparent" : card.gradient,
        backgroundImage: hasTemplate ? `url("${template}")` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/*
        Ambient glow — только для градиентного варианта. На картинке-шаблоне
        такой глюк рисует лишнее «пятно» поверх дизайна.
       */}
      {!hasTemplate && (
        <Box
          sx={{
            position: "absolute",
            right: "-40px",
            top: "-40px",
            width: "220px",
            height: "220px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 70%)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Frozen overlay */}
      {isFrozen && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(41, 88, 160, 0.55) 0%, rgba(30, 60, 120, 0.65) 100%)",
            backdropFilter: "blur(1.5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              padding: "6px 14px",
              borderRadius: "999px",
              bgcolor: "rgba(255,255,255,0.16)",
              color: "#fff",
              letterSpacing: "0.5px",
            }}
          >
            {t("visual.frozen_overlay")}
          </Typography>
        </Box>
      )}

      {/*
        Верхний ряд.

        Без шаблона (градиентная карта нашей типографики):
          провайдер слева большим кеглем + пилюля статуса справа. Как раньше.

        С шаблоном:
          - Название провайдера справа-вверху приглушено (маленькое,
            opacity 0.7) — это подпись «в чей карточной программе я»,
            а не заголовок карты. Заголовок уже нарисован в самом шаблоне.
          - Пилюля статуса показывается только для нестандартных состояний
            (processing, frozen) — на ACTIVE лишний UI на дизайне не рисуем.
          - На шаблоне размер шрифтов заметно меньше, чем на градиенте:
            типографика самого шаблона всегда крупнее и должна доминировать.
       */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: hasTemplate ? "flex-end" : "space-between",
          position: "relative",
          zIndex: 1,
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontSize: hasTemplate
              ? isLarge
                ? "11px"
                : "10px"
              : isLarge
              ? "15px"
              : "14px",
            fontWeight: hasTemplate ? 600 : 700,
            letterSpacing: hasTemplate ? "0.6px" : "0px",
            opacity: hasTemplate ? 0.7 : 0.95,
            textTransform: hasTemplate ? "uppercase" : "none",
            textAlign: hasTemplate ? "right" : "left",
          }}
        >
          {hasTemplate ? (
            card.provider.toUpperCase()
          ) : (
            <>
              <Box component="span" sx={{ opacity: 0.9 }}>
                ✱
              </Box>{" "}
              {card.provider.toUpperCase()}
            </>
          )}
        </Typography>
        {!hasTemplate && <StatusPill status={effectiveStatus} label={statusText[effectiveStatus]} />}
      </Box>

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          sx={{
            // На шаблоне номер печатается заметно меньше — крупный номер
            // ломает вёрстку кастомного дизайна и «перекрикивает» его.
            fontSize: hasTemplate
              ? isLarge
                ? "18px"
                : "15px"
              : isLarge
              ? "26px"
              : "22px",
            fontWeight: 600,
            letterSpacing: hasTemplate ? "1.2px" : "2px",
            marginBottom: "8px",
            opacity: isProcessing ? 0.5 : 1,
          }}
        >
          •••• {card.lastFour}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: hasTemplate ? "9px" : "10px",
                color: "rgba(255, 255, 255, 0.6)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {isProcessing ? t("visual.holder") : t("visual.balance")}
            </Typography>
            <Typography
              sx={{
                fontSize: hasTemplate
                  ? isLarge
                    ? "14px"
                    : "13px"
                  : isLarge
                  ? "17px"
                  : "15px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {isProcessing
                ? card.cardholderName
                : `${card.balance.toFixed(2)} ${card.currency}`}
            </Typography>
          </Box>
          {/*
            Правый нижний угол.
            - Без шаблона: BrandLogo (VISA/MC/UnionPay) — историческое поведение.
            - С шаблоном: логотип бренда уже нарисован в самой картинке, не
              дублируем. Показываем пилюлю статуса ТОЛЬКО если карта не в
              обычном ACTIVE — на ACTIVE рисовать «Активна» на кастомном
              дизайне — визуальный шум.
           */}
          {hasTemplate
            ? effectiveStatus !== "active" && (
                <StatusPill
                  status={effectiveStatus}
                  label={statusText[effectiveStatus]}
                />
              )
            : <BrandLogo brand={card.brand} />}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Пилюля статуса — точка + подпись. Вынесена отдельно, потому что
 * используется в двух местах (правый-верх на градиенте, правый-низ на
 * шаблоне) и с разным поведением показа.
 */
function StatusPill({ status, label }: { status: CardStatus; label: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "3px 10px",
        borderRadius: "999px",
        bgcolor: "rgba(0, 0, 0, 0.32)",
        backdropFilter: "blur(4px)",
        // Тонкий white border, чтобы пилюля не сливалась ни со светлыми,
        // ни с тёмными шаблонами.
        border: "1px solid rgba(255, 255, 255, 0.12)",
      }}
    >
      <Box
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          bgcolor: STATUS_COLOR[status],
        }}
      />
      <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#fff" }}>
        {label}
      </Typography>
    </Box>
  );
}
